/**
 * "Perfis de coleta": one card per collection profile. A card names the profile, its current
 * usage (accounts and their estimated daily credits) and, per media kind, which extras it
 * collects and the timeline a post walks through -- the same period chips the editor draws.
 *
 * Pausing or resuming a profile and deleting one go through a dialog that names the accounts
 * the action reaches; a profile still in use is deleted only by naming the profile its
 * accounts move to.
 */

import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, Pencil } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { ConfirmDialog } from '../../../../chrome/form';
import { EntityRow } from '../../../../chrome/entity/EntitySwitcher';
import { IconHint } from '../../../../chrome/hint';
import { EmptyState } from '../../../../chrome/state';
import { getLocale, type Locale } from '../../../../i18n';
import { integer } from '../../../../i18n/format';
import type { ApiFailure, ApiFailureError } from '../../../../lib/http/errors';
import { Badge } from '../../../../ui/badge';
import { Button } from '../../../../ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Chip } from '../../../../ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../ui/dropdown-menu';
import { Field, FieldLabel } from '../../../../ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/select';
import { Separator } from '../../../../ui/separator';
import { Skeleton } from '../../../../ui/skeleton';
import { PlatformIcon } from '../../../campaign/accounts/NoSelection';
import { useApiClient } from '../../../session/client';
import { PROFILE_EDITOR_PATH } from '../../paths';
import {
  ct,
  ctPlural,
  mediaKindSchema,
  monitoringProfileOutSchema,
  profileMediaSchema,
  PROFILES_PATH,
  PROFILES_QUERY_KEY,
  type MonitoringProfileListed,
  type ProfileUsageExample,
} from '../Catalogue';
import { PeriodSummary } from '../profile/PeriodSummary';
import { ctt } from './messages';

type ProfileMedia = MonitoringProfileListed['media'][number];
type FlagKey = Extract<keyof ProfileMedia, `${string}_enabled`>;

/** Every boolean `*_enabled` switch a media row carries, read off the schema so a new one
 *  shows up here without a list to keep in step. A function, not a constant: `Catalogue.tsx`
 *  imports this module before it defines the schema. */
export function mediaFlags(): FlagKey[] {
  return Object.keys(profileMediaSchema.shape).filter((key) =>
    key.endsWith('_enabled'),
  ) as FlagKey[];
}

const FLAG_LABEL: Record<FlagKey, () => string> = {
  comments_enabled: () => ct('flag.comments_enabled'),
  likers_enabled: () => ct('flag.likers_enabled'),
};

const ENABLEMENT_LABEL = {
  on: () => ct('kind.on'),
  off: () => ct('kind.off'),
  auto: () => ct('kind.auto'),
} as const;

function exampleOption(example: ProfileUsageExample) {
  const handle = example.handle === null ? null : `@${example.handle}`;
  return {
    id: example.id,
    name: example.label ?? handle ?? '—',
    detail: example.label === null ? null : handle,
    imageUrl: example.avatar_url,
    mark: <PlatformIcon platform={example.platform} className="size-3" />,
  };
}

/** Up to five accounts as entity rows, then how many more there are. */
function UsageExamples(props: { profile: MonitoringProfileListed }): ReactNode {
  const { profile } = props;
  const more = profile.usage_count - profile.usage_examples.length;
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {profile.usage_examples.map((example) => (
          <li key={example.id} className="flex min-w-0 items-center gap-2 text-body-sm">
            <EntityRow option={exampleOption(example)} />
          </li>
        ))}
      </ul>
      {more > 0 && (
        <p className="text-caption text-muted-foreground">
          {ctPlural('usage.examplesMore.other', more, { count: more })}
        </p>
      )}
    </div>
  );
}

function Stat(props: { label: string; value: string; testId?: string }): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-caption text-muted-foreground">{props.label}</dt>
      <dd data-numeric data-testid={props.testId} className="text-title-sm">
        {props.value}
      </dd>
    </div>
  );
}

function KindBlock(props: { row: ProfileMedia }): ReactNode {
  const { row } = props;
  const kindName = ctt(`kind.${row.media_kind}`);
  const flags = row.media_kind === 'STORY' ? [] : mediaFlags();
  const off = row.enabled === 'off';
  return (
    <section aria-label={kindName} className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-label">{kindName}</h4>
        <Chip size="sm" tone={off ? 'neutral' : 'positive'}>
          {ENABLEMENT_LABEL[row.enabled]()}
        </Chip>
      </div>
      {flags.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {flags.map((flag) => (
            <li key={flag}>
              <Chip
                size="sm"
                tone={row[flag] === true ? 'positive' : 'neutral'}
                data-flag={flag}
                data-on={row[flag] === true}
                aria-label={ct(row[flag] === true ? 'flag.on' : 'flag.off', {
                  flag: FLAG_LABEL[flag](),
                })}
              >
                {FLAG_LABEL[flag]()}
              </Chip>
            </li>
          ))}
        </ul>
      )}
      {!off && (
        <ol aria-label={ct('kind.timeline', { kind: kindName })} className="flex flex-wrap gap-1.5">
          {row.media_kind === 'STORY'
            ? row.story_interval_seconds !== null && (
                <li>
                  <PeriodSummary
                    frequencyOnly
                    period={{
                      frequency_seconds: row.story_interval_seconds,
                      duration_seconds: null,
                      color: null,
                    }}
                  />
                </li>
              )
            : row.periods.map((period, index) => (
                <li key={index}>
                  <PeriodSummary period={period} />
                </li>
              ))}
        </ol>
      )}
    </section>
  );
}

function AudioBlock(props: { audio: MonitoringProfileListed['audio'] }): ReactNode {
  const { audio } = props;
  const kindName = ctt('kind.AUDIO');
  return (
    <section aria-label={kindName} className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-label">{kindName}</h4>
        <Chip size="sm" tone={audio.enabled ? 'positive' : 'neutral'}>
          {ct(audio.enabled ? 'kind.on' : 'kind.off')}
        </Chip>
      </div>
      {audio.enabled && (
        <ol aria-label={ct('kind.timeline', { kind: kindName })} className="flex flex-wrap gap-1.5">
          {audio.periods.map((period, index) => (
            <li key={index}>
              <PeriodSummary period={period} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ProfileMenu(props: {
  profile: MonitoringProfileListed;
  editHref: { to: string; params: Record<string, string> };
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}): ReactNode {
  const { profile } = props;
  return (
    <DropdownMenu>
      <IconHint label={ct('actions.label', { name: profile.name })}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon">
            <Ellipsis aria-hidden />
          </Button>
        </DropdownMenuTrigger>
      </IconHint>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={props.onToggle}>
          {ct(profile.active ? 'action.pause' : 'action.resume')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={props.onDuplicate}>{ct('action.duplicate')}</DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={props.editHref.to} params={props.editHref.params}>
            {ct('action.edit')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={props.onDelete}>
          {ct('action.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProfileCard(props: {
  tenantId: string;
  profile: MonitoringProfileListed;
  locale: Locale;
  canWrite: boolean;
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}): ReactNode {
  const { profile, locale } = props;
  const titleId = useId();
  const editHref = {
    to: PROFILE_EDITOR_PATH,
    params: { tenantId: props.tenantId, profileId: profile.id },
  };
  const kinds = mediaKindSchema.options.flatMap(
    (kind) => profile.media.find((row) => row.media_kind === kind) ?? [],
  );
  return (
    <Card role="article" aria-labelledby={titleId} data-testid={`profile-card-${profile.id}`}>
      <CardHeader className="flex items-center gap-2">
        <CardTitle className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            id={titleId}
            to={editHref.to}
            params={editHref.params}
            className="truncate rounded-sm hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {profile.name}
          </Link>
          {!profile.active && <Badge variant="warning">{ct('status.paused')}</Badge>}
        </CardTitle>
        {props.canWrite && (
          <CardAction className="ml-auto flex shrink-0 items-center gap-1">
            <Button asChild variant="outline" size="sm">
              <Link
                to={editHref.to}
                params={editHref.params}
                aria-label={ct('action.editLabel', { name: profile.name })}
              >
                <Pencil aria-hidden />
                {ct('action.edit')}
              </Link>
            </Button>
            <ProfileMenu
              profile={profile}
              editHref={editHref}
              onToggle={props.onToggle}
              onDuplicate={props.onDuplicate}
              onDelete={props.onDelete}
            />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <section aria-label={ct('usage.title')} className="flex flex-col gap-3">
          <h4 className="text-label">{ct('usage.title')}</h4>
          <dl className="grid grid-cols-2 gap-4">
            <Stat
              label={ct('usage.accounts')}
              value={integer(profile.usage_count, locale)}
              testId="usage-accounts"
            />
            <Stat
              label={ct('usage.credits')}
              value={pricedInteger(profile.usage_credits_per_day, locale)}
              testId="usage-credits"
            />
          </dl>
        </section>
        <Separator />
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
          {kinds.map((row) => (
            <KindBlock key={row.media_kind} row={row} />
          ))}
          <AudioBlock audio={profile.audio} />
        </div>
      </CardContent>
    </Card>
  );
}

/** `showCredits` is off when a collection-change statement carries the figure instead. */
function Impact(props: {
  profile: MonitoringProfileListed;
  pausing: boolean;
  showCredits?: boolean;
}): ReactNode {
  const locale = getLocale();
  const { profile } = props;
  if (profile.usage_count === 0) {
    return <p className="text-body-sm text-muted-foreground">{ct('usage.none')}</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-4">
        <Stat
          label={ct('impact.accounts')}
          value={integer(profile.usage_count, locale)}
          testId="impact-accounts"
        />
        {props.showCredits !== false && (
          <Stat
            label={ct(props.pausing ? 'impact.saves' : 'impact.costs')}
            value={pricedInteger(profile.usage_credits_per_day, locale)}
            testId="impact-credits"
          />
        )}
      </dl>
      <UsageExamples profile={profile} />
    </div>
  );
}

/** A dialog's failure in this screen's own words: an `invalid` or `conflict` failure would
 *  otherwise show the server's text. */
function ownFailure(error: ApiFailureError | null, message: string): ApiFailure | null {
  if (error === null) return null;
  const { failure } = error;
  return failure.kind === 'invalid' || failure.kind === 'conflict'
    ? { kind: 'invalid', field: null, message }
    : failure;
}

function ToggleDialog(props: {
  tenantId: string;
  profile: MonitoringProfileListed;
  onClose: () => void;
}): ReactNode {
  const { profile } = props;
  const pausing = profile.active;
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation<unknown, ApiFailureError>({
    mutationFn: () =>
      client.patchParsed(
        `${PROFILES_PATH(props.tenantId)}/${profile.id}`,
        { active: !profile.active },
        monitoringProfileOutSchema,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEY(props.tenantId) }),
  });
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={ct(pausing ? 'pause.title' : 'resume.title', { name: profile.name })}
      description={ct(pausing ? 'pause.body' : 'resume.body')}
      confirmLabel={ct(pausing ? 'action.pause' : 'action.resume')}
      pending={mutation.isPending}
      failure={ownFailure(mutation.error, ct('toggle.failed'))}
      onConfirm={() => mutation.mutateAsync()}
    >
      <Impact profile={profile} pausing={pausing} />
    </ConfirmDialog>
  );
}

function DeleteDialog(props: {
  tenantId: string;
  profile: MonitoringProfileListed;
  others: MonitoringProfileListed[];
  onClose: () => void;
}): ReactNode {
  const { profile } = props;
  const client = useApiClient();
  const queryClient = useQueryClient();
  const targetId = useId();
  const [target, setTarget] = useState<string | null>(null);
  const targetProfile = props.others.find((other) => other.id === target) ?? null;
  const mutation = useMutation<void, ApiFailureError>({
    mutationFn: () => {
      const params = new URLSearchParams();
      if (target !== null) params.set('reassign_to', target);
      if (change !== null) params.set('confirm_collection_change', 'true');
      const query = params.toString() === '' ? '' : `?${params.toString()}`;
      return client.del(`${PROFILES_PATH(props.tenantId)}/${profile.id}${query}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEY(props.tenantId) }),
  });
  const serverChange =
    mutation.error?.failure.kind === 'conflict'
      ? mutation.error.failure.collectionChange
      : undefined;
  // Moving accounts onto a profile in the other collection state pauses or resumes them: the
  // dialog says so up front, and the api's refusal says it again if this list was stale.
  const change: { kind: 'resume' | 'pause'; accounts: number } | null =
    serverChange !== undefined
      ? { kind: serverChange.change, accounts: serverChange.accounts }
      : targetProfile !== null && profile.usage_count > 0 && targetProfile.active !== profile.active
        ? { kind: targetProfile.active ? 'resume' : 'pause', accounts: profile.usage_count }
        : null;
  // A campaign default still naming the profile is a reference the listing does not count.
  const referencedElsewhere =
    mutation.error?.failure.kind === 'conflict' &&
    serverChange === undefined &&
    profile.usage_count === 0;
  const needsTarget = profile.usage_count > 0 || referencedElsewhere;
  const failure =
    referencedElsewhere || serverChange !== undefined
      ? null
      : ownFailure(mutation.error, ct('delete.failed'));

  return (
    <ConfirmDialog
      open
      destructive
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={ct('delete.title', { name: profile.name })}
      description={ct('delete.body')}
      confirmLabel={ct('action.delete')}
      pending={mutation.isPending}
      failure={failure}
      confirmDisabled={needsTarget && target === null}
      onConfirm={() => mutation.mutateAsync()}
    >
      {needsTarget && (
        <div className="flex flex-col gap-4">
          <p className="text-body-sm">
            {ct(referencedElsewhere ? 'delete.inUseElsewhere' : 'delete.inUse')}
          </p>
          {profile.usage_count > 0 && (
            <Impact profile={profile} pausing showCredits={change === null} />
          )}
          <Field>
            <FieldLabel htmlFor={targetId}>{ct('delete.target')}</FieldLabel>
            <Select
              value={target ?? undefined}
              onValueChange={(next) => {
                setTarget(next);
                mutation.reset();
              }}
            >
              <SelectTrigger id={targetId} aria-label={ct('delete.target')}>
                <SelectValue placeholder={ct('delete.targetPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {props.others.map((other) => (
                  <SelectItem key={other.id} value={other.id}>
                    {other.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {change !== null && (
            <CollectionChange
              change={change.kind}
              accounts={change.accounts}
              credits={changeCredits(
                change.kind === 'resume' ? targetProfile : profile,
                change.accounts,
              )}
            />
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

function CollectionChange(props: {
  change: 'resume' | 'pause';
  accounts: number;
  credits: number | null;
}): ReactNode {
  const resuming = props.change === 'resume';
  return (
    <div role="status" className="flex flex-col gap-3">
      <p className="text-body-sm font-medium">
        {ctPlural(`collectionChange.${props.change}.other`, props.accounts, {
          count: props.accounts,
        })}
      </p>
      <dl className="grid grid-cols-2 gap-4">
        <Stat
          label={ct(resuming ? 'impact.costs' : 'impact.saves')}
          value={pricedInteger(props.credits, getLocale())}
          testId="collection-change-credits"
        />
      </dl>
    </div>
  );
}

/** A figure in platform credits, or "sem preço" where an operation it runs has no rate. */
function pricedInteger(value: number | null, locale: Locale): string {
  return value === null ? ct('unpriced') : integer(value, locale);
}

/** What the accounts moved cost a day on `profile`'s own price; none when there is no such
 *  profile to price them on, and `null` while the price is withheld. */
function changeCredits(profile: MonitoringProfileListed | null, accounts: number): number | null {
  if (profile === null) return 0;
  return profile.price.credits_per_day === null ? null : profile.price.credits_per_day * accounts;
}

type OpenDialog = { kind: 'toggle' | 'delete'; profileId: string } | null;

export function ProfileCards(props: {
  tenantId: string;
  profiles: MonitoringProfileListed[];
  loading: boolean;
  canWrite: boolean;
  onDuplicate: (profile: MonitoringProfileListed) => void;
}): ReactNode {
  const locale = getLocale();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const open = props.profiles.find((profile) => profile.id === dialog?.profileId) ?? null;

  if (props.loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="profiles-loading">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (props.profiles.length === 0) {
    return <EmptyState title={ct('profiles.empty')} />;
  }
  return (
    <>
      <ul aria-label={ct('profiles.label')} className="flex flex-col gap-4">
        {props.profiles.map((profile) => (
          <li key={profile.id}>
            <ProfileCard
              tenantId={props.tenantId}
              profile={profile}
              locale={locale}
              canWrite={props.canWrite}
              onToggle={() => setDialog({ kind: 'toggle', profileId: profile.id })}
              onDuplicate={() => props.onDuplicate(profile)}
              onDelete={() => setDialog({ kind: 'delete', profileId: profile.id })}
            />
          </li>
        ))}
      </ul>
      {open !== null && dialog?.kind === 'toggle' && (
        <ToggleDialog
          key={open.id}
          tenantId={props.tenantId}
          profile={open}
          onClose={() => setDialog(null)}
        />
      )}
      {open !== null && dialog?.kind === 'delete' && (
        <DeleteDialog
          key={open.id}
          tenantId={props.tenantId}
          profile={open}
          others={props.profiles.filter((profile) => profile.id !== open.id)}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}
